<template>
  <span class="zm-checkbox" :class="{'zm-checkbox--large': checkboxSize === 'large'}">
    <span class="zm-checkbox__wrap">

      <label
        class="zm-checkbox__label"
        :class="[
          { 'is-disabled': isDisabled },
          { 'is-checked': isChecked },
          { 'is-indeterminate': indeterminate },
          { 'is-disable-transition': disableTransition}
        ]"
      >
        <input
          ref="input"
          class="zm-checkbox__original"
          type="checkbox"
          :disabled="isDisabled"
          :value="label"
          :name="name"
          :id="itemId"
          v-model="model"
          :aria-checked="indeterminate ? 'mixed': ((typeof isChecked === 'undefined')? 'false' : isChecked.toString())"
          :aria-label="ariaLabel || label"
          :aria-describedby="ariaLabelBy"
          @change="handleChange"
          @focus="focused = true"
          @blur="focused = false"
          @keydown.enter.stop="handleEnter"
          :tabindex="isList ? -1 : tabindex ? tabindex : 0"
          :aria-controls="controls"
        >
        <span class="zm-checkbox__inner">
          <i class="zm-checkbox__knob"></i>
        </span>
        <span class="zm-checkbox__label-inner" v-if="$slots.default || label">
          <slot></slot>
          <template v-if="!$slots.default">{{label}}</template>
        </span>
        <span v-else-if="ariaLabel" class="zm-sr-only">{{ariaLabel}}</span>
      </label>
      <span class="zm-checkbox__suffix" v-if="$slots.suffix">
        <slot name="suffix"></slot>
      </span>
    </span>
    <slot v-if="$slots.child" name="child"></slot>
  </span>
</template>
<script>
  import Emitter from '../../mixins/emitter';

  let _uid = 0;

  export default {
    name: 'ZmCheckbox',

    mixins: [Emitter],

    inject: {
      ZmForm: {
        default: ''
      },
      ZmFormItem: {
        default: ''
      }
    },

    componentName: 'ZmCheckbox',

    data() {
      return {
        selfModel: false,
        focused: false,
        isLimitExceeded: false,
        isGroup: false,
        isList: false
      };
    },

    computed: {
      model: {
        get() {
          return this.isGroupOrList
            ? this.store : this.value !== undefined ? this.value : this.selfModel;
        },

        set(val) {
          if (this.isGroup) {
            this.dispatch('ZmCheckboxGroup', 'input', [val]);
          } else if (this.isList) {
            this.dispatch('ZmCheckboxList', 'input', [val]);
          } else {
            this.$emit('input', val);
            this.selfModel = val;
          }
        }
      },

      isChecked() {
        if ({}.toString.call(this.model) === '[object Boolean]') {
          return this.model;
        } else if (Array.isArray(this.model)) {
          return this.model.indexOf(this.label) > -1;
        } else if (this.model !== null && this.model !== undefined) {
          return this.model === true;
        }
        return false;
      },

      isGroupOrList() {
        return this.isList || this.isGroup;
      },

      store() {
        return this._checkboxGroupOrList ? this._checkboxGroupOrList.value : this.value;
      },

      isDisabled() {
        return this.isGroupOrList
          ? this._checkboxGroupOrList.disabled || this.disabled || (this.ZmForm || {}).disabled || (!this.isChecked && this.isExceeded())
          : this.disabled || (this.ZmForm || {}).disabled;
      },

      _elFormItemSize() {
        return (this.ZmFormItem || {}).elFormItemSize;
      },

      checkboxSize() {
        const temCheckboxSize = this._elFormItemSize || (this.$ELEMENT || {}).size || this.size;
        return this.isGroup
          ? this._checkboxGroupOrList.checkboxGroupSize || temCheckboxSize
          : temCheckboxSize;
      },

      itemId() {
        return this.id || `checkbox_${_uid++}`;
      }
    },

    props: {
      value: {}, // eslint-disable-line vue/require-prop-types
      label: [String, Number, Boolean, Object],
      x: Number,
      y: Number,
      indeterminate: Boolean,
      disabled: Boolean,
      checked: Boolean,
      ariaLabel: String,
      ariaLabelBy: String,
      name: String,
      tabindex: [Number, String],
      id: String,
      controls: String,
      disableTransition: Boolean
    },

    methods: {
      focus() {
        this.focused = true;
        this.$refs.input.focus();
      },
      blur() {
        this.focused = false;
        this.$refs.input.focus();
      },
      addToStore() {
        if (Array.isArray(this.model) && this.model.indexOf(this.label) === -1) {
          this.model.push(this.label);
        } else {
          this.model = true;
        }
      },
      handleEnter(ev) {
        ev.target.checked = !ev.target.checked;
        if (this.isGroup || this.isList) {
          if (!Array.isArray(this.model)) {
            console.warn('Checkbox group or checkbox list model must be an array');
          } else {
            const clone = this.model.slice(0);
            if (clone.indexOf(this.label) === -1) {
              clone.push(this.label);
            } else {
              const index = clone.indexOf(this.label);
              clone.splice(index, 1);
            }
            this.model = clone;
          }
        } else {
          this.model = ev.target.checked;
        }
        this.handleChange(ev);
      },
      handleChange(ev) {
        if (this.isLimitExceeded) return;
        let value;
        if (ev.target.checked) {
          value = true;
        } else {
          value = false;
        }
        this.$emit('change', value, ev);
        this.$nextTick(() => {
          if (this.isGroup) {
            this.dispatch('ZmCheckboxGroup', 'change', [this._checkboxGroupOrList.value]);
          } else if (this.isList) {
            this.dispatch('ZmCheckboxList', 'change', [this._checkboxGroupOrList.value, this]);
          }
        });
      },
      isExceeded() {
        const limit = this._checkboxGroupOrList.limit;
        if (limit > 0) {
          return this.model.length >= limit;
        }
        return false;
      }
    },

    created() {
      this.checked && this.addToStore();

      this.isGroup = false;
      this.isList = false;
      let parent = this.$parent;
      while (parent) {
        const componentName = parent.$options.componentName;
        if (componentName !== 'ZmCheckboxGroup' && componentName !== 'ZmCheckboxList') {
          parent = parent.$parent;
        } else {
          if (componentName === 'ZmCheckboxGroup') {
            this.isGroup = true;
          } else {
            this.isList = true;
          }
          this._checkboxGroupOrList = parent;
          break;
        }
      }
    },
    /*
    mounted() { // 为indeterminate元素 添加aria-controls 属性
      if (this.indeterminate) {
        this.$el.setAttribute('aria-controls', this.controls);
      }
    },
    */

    watch: {
      value(value) {
        this.dispatch('ZmFormItem', 'el.form.change', value);
      }
    }
  };
</script>

<style lang="scss">
  @import "./style";
</style>
